import React, { Fragment } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { PreBookingSettings } from '../../../../core/api/models';
import ErrorMessage from '../../../../components/text/ErrorMessage';
import { DropdownList } from 'react-widgets';

const UpdateForm: React.FunctionComponent<{
  onSubmit: (data: PreBookingSettings) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  data?: PreBookingSettings;
  error?: string;
}> = (props) => {
  const { control, handleSubmit, errors, watch } = useForm<PreBookingSettings>({
    reValidateMode: 'onChange',
    mode: 'onSubmit',
    defaultValues: props.data
  });

  const sameCalendarMonthOptionEnabled = watch('sameCalendarMonthOptionEnabled');

  const renderPreBookingExpiry = () => {
    return (
      <div className="col-12">
        <label className="font-sm text-uppercase">Pre-Booking Expiry</label>
        <div className="row">
          <div className="col-6">
            <div className="form-group font-weight-semibold mb-3">
              <Controller
                control={control}
                rules={{
                  required: 'Pre-Booking Expiry is required',
                  validate: {
                    numberOnly: (value) =>
                      new RegExp('\\d+').test(value) || 'Invalid value',
                    min: (value) =>
                      Number(value) >= 0 || 'Value shoud be positive',
                  },
                }}
                name="expiry.value"
                defaultValue={props.data?.expiry?.value}
                render={({ onChange, value, name }) => (
                  <input
                    type="number"
                    min={0}
                    className="form-control rounded-sm font-weight-semibold"
                    placeholder="Pre-Booking Expiry"
                    name={name}
                    value={value}
                    onChange={onChange}
                    id="expiry.value"
                  />
                )}
              />
              <ErrorMessage>{errors.expiry?.value?.message}</ErrorMessage>
            </div>
          </div>
          <div className="col-6">
            <div className="form-group font-weight-semibold mb-3">
              <Controller
                control={control}
                name="expiry.unit"
                defaultValue={props.data?.expiry?.unit || 'Hours'}
                render={({ onChange, value, name }) => (
                  <DropdownList
                    data={[
                      {
                        label: 'Minutes',
                        value: 'Minutes',
                      },
                      {
                        label: 'Hours',
                        value: 'Hours',
                      },
                      {
                        label: 'Days',
                        value: 'Days',
                      },
                    ]}
                    id="expiry.unit"
                    placeholder="Unit"
                    name={name}
                    defaultValue={'Hours'}
                    value={value}
                    onChange={(val) => onChange(val.value)}
                    textField="label"
                    valueField="value"
                  />
                )}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderButton = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3 text-right pt-3">
          <button
            type="button"
            className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
            onClick={props.onCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary py-2 font-weight-semibold font-normal px-4 "
            disabled={props.isSubmitting}
          >
            Update
          </button>
        </div>
      </div>
    );
  };

  const renderSameCalendarMonthOptionEnabled = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <Controller
            control={control}
            name="sameCalendarMonthOptionEnabled"
            defaultValue={
              props.data?.sameCalendarMonthOptionEnabled
            }
            render={({ onChange, value, name }) => (
              <Fragment>
                <input
                  type="checkbox"
                  id={name}
                  checked={value}
                  onChange={(e) => onChange(e.target.checked)}
                />
                <label htmlFor={name}>
                  Same Calendar Month Option Enabled
                </label>
              </Fragment>
            )}
          />
          <ErrorMessage>
            {errors.sameCalendarMonthOptionEnabled?.message}
          </ErrorMessage>
        </div>
      </div>
    );
  };

  const renderCampaignDifferentiatorTimeline = () => {
    return (
      <div className="col-12">
        <label className="font-sm text-uppercase">Campaign Differentiator Timeline</label>
        <div className="row">
          <div className="col-6">
            <div className="form-group font-weight-semibold mb-3">
              <Controller
                control={control}
                rules={{
                  required: 'Campaign Differentiator Timeline is required',
                  validate: {
                    numberOnly: (value) =>
                      new RegExp('\\d+').test(value) || 'Invalid value',
                    min: (value) =>
                      Number(value) >= 0 || 'Value shoud be positive',
                  },
                }}
                name="campaignDifferentiatorTimeline.value"
                defaultValue={props.data?.campaignDifferentiatorTimeline?.value}
                render={({ onChange, value, name }) => (
                  <input
                    type="number"
                    min={0}
                    className="form-control rounded-sm font-weight-semibold"
                    placeholder="Campaign Differentiator Timeline"
                    name={name}
                    value={value}
                    onChange={onChange}
                    id="campaignDifferentiatorTimeline.value"
                  />
                )}
              />
              <ErrorMessage>{errors.campaignDifferentiatorTimeline?.value?.message}</ErrorMessage>
            </div>
          </div>
          <div className="col-6">
            <div className="form-group font-weight-semibold mb-3">
              <Controller
                control={control}
                name="campaignDifferentiatorTimeline.unit"
                defaultValue={props.data?.campaignDifferentiatorTimeline?.unit || 'Days'}
                render={({ onChange, value, name }) => (
                  <DropdownList
                    data={[
                      {
                        label: 'Minutes',
                        value: 'Minutes',
                      },
                      {
                        label: 'Hours',
                        value: 'Hours',
                      },
                      {
                        label: 'Days',
                        value: 'Days',
                      },
                    ]}
                    id="campaignDifferentiatorTimeline.unit"
                    placeholder="Unit"
                    name={name}
                    defaultValue={'Hours'}
                    value={value}
                    onChange={(val) => onChange(val.value)}
                    textField="label"
                    valueField="value"
                  />
                )}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit(props.onSubmit)}>
      <div className="p-4">
        <h4 className="mb-3 font-weight-semibold">Update Pre-Booking Settings</h4>
        <div className="row gutter-10">
          {renderPreBookingExpiry()}
          {renderSameCalendarMonthOptionEnabled()}
          {!sameCalendarMonthOptionEnabled && renderCampaignDifferentiatorTimeline()}
          <ErrorMessage>{props.error}</ErrorMessage>
          {renderButton()}
        </div>
      </div>
    </form>
  );
};

export default UpdateForm;
